import React from 'react'
import { css } from 'styled-components'

import { Shimmer } from './Shimmer'
import useTimeout from './hooks/useTimeout'

export const ProductDefaultImage = 'https://imgix.dispenseapp.com/default-image.png'

type ImageProps = {
  alt?: string
  src?: string
  height?: string | number
  width?: string | number
  fallbackSrc?: string
  className?: string
  loading?: 'lazy' | 'eager'
  onLoad?: () => void
}

export function Image(props: ImageProps) {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const [imageSrc, setImageSrc] = React.useState(props.src)

  React.useEffect(() => {
    setImageSrc(props.src ?? '')
  }, [props.src])

  useTimeout(() => {
    // istanbul ignore next
    setStatus('success')
  }, 1000)

  return (
    <div
      css={css`
        border-radius: var(--border-radius-xs);
        border: 1px solid transparent;
        overflow: hidden;
        flex-shrink: 0;
        outline: none;
        position: relative;
        width: min-content;

        img,
        div {
          border-radius: var(--border-radius-xs);
        }
      `}
      className={props.className}
      data-testid="image-container"
    >
      <img
        onError={() => {
          setStatus('error')

          if (props.fallbackSrc) {
            setImageSrc(props.fallbackSrc)
          } else {
            setImageSrc(ProductDefaultImage)
          }
        }}
        onLoadStart={() => setStatus('loading')}
        onLoad={() => {
          setStatus('success')

          if (props.onLoad) props.onLoad()
        }}
        data-testid="image"
        alt={props.alt}
        width={props.width}
        height={props.height}
        loading={props.loading ?? 'lazy'}
        css={css`
          object-fit: cover;
          display: block;
          opacity: ${status === 'success' ? 1 : 0};
          transition: opacity 200ms ease-out;
          @media (prefers-reduced-motion: reduce) { 
            transition: none;
          }
        `}
        aria-hidden={props.alt ? undefined : true}
        src={imageSrc}
      />
      <Shimmer
        aria-hidden
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          opacity: ${status === 'success' ? 0 : 1};
          transition: opacity 200ms ease-out;
        `}
      />
    </div>
  )
}
